import { useEffect } from 'react';

/**
 * useScrollReveal — global scroll-driven animation hook.
 *
 * Watches for two utility classes:
 *   - `.reveal` → fades up into view (adds `.is-visible`)
 *   - `.scroll-zoom` → scales from 0.92 → 1 as it enters viewport (adds `.is-zoomed`)
 *
 * Modern browsers (Chrome 115+) get a smoother CSS scroll-driven animation
 * via @supports (animation-timeline: view()) — see index.css.
 * Older browsers fall back to the IntersectionObserver-driven version here.
 *
 * Mount once at the App level. The observer is created on mount and
 * disconnected on unmount. Respects prefers-reduced-motion via CSS.
 */
export function useScrollReveal({ rootMargin = '0px 0px -10% 0px', threshold = 0.1 } = {}) {
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      // Fallback: just reveal everything immediately if IntersectionObserver isn't available
      document.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'));
      document.querySelectorAll('.scroll-zoom').forEach((el) => el.classList.add('is-zoomed'));
      return;
    }

    const supportsScrollTimeline = CSS && CSS.supports && CSS.supports('animation-timeline: view()');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            if (el.classList.contains('reveal')) {
              el.classList.add('is-visible');
            }
            if (el.classList.contains('scroll-zoom') && !supportsScrollTimeline) {
              el.classList.add('is-zoomed');
            }
            observer.unobserve(el);
          }
        });
      },
      { rootMargin, threshold }
    );

    const observe = () => {
      document
        .querySelectorAll('.reveal:not(.is-visible), .scroll-zoom:not(.is-zoomed)')
        .forEach((el) => observer.observe(el));
    };

    observe();
    const rescanTimer = setTimeout(observe, 200);

    return () => {
      clearTimeout(rescanTimer);
      observer.disconnect();
    };
  }, [rootMargin, threshold]);
}
